import {
  kvDel,
  kvGet,
  kvGetSyncFromRemote,
  kvSet,
  kvZadd, kvZoperationsSyncFromRemote,
  kvZrange,
  kvZremrangebyscore
} from '@/lib/storage/kv-client';
import { SyncAction, SyncEvent, SyncMetadata } from '@/lib/storage/types.ts';
import { DeviceInfo, getDeviceIdFromLocalStorage } from '@/providers/device-context.tsx';


export class SyncService {
  private static readonly SYNC_WINDOW_DAYS = 7;

  private static getUserEventsKey(userId: string) {
    return `user:${userId}:events`;
  }

  private static getDeviceSyncKey(userId: string, deviceId: string) {
    return `user:${userId}:device:${deviceId}:sync`;
  }

  static getUserDeviceListKey(userId: string) {
    return `user:${userId}:deviceList`;  // Stores array of deviceIds
  }

  static getUserDeviceDataKey(userId: string, deviceId: string) {
    return `user:${userId}:device:${deviceId}:data`;  // Stores device data
  }

  static async registerDevice(
    userId: string,
    deviceId: string,
    deviceData: DeviceInfo
  ): Promise<void> {
    try {
      // Get current device list
      const existingDevices = await kvGet<string[]>(this.getUserDeviceListKey(userId)) ?? [];

      // Add device to list if not present
      if (!existingDevices.includes(deviceId)) {
        await kvSet(
          this.getUserDeviceListKey(userId),
          [...existingDevices, deviceId]
        );
        // add sync as now as its a new device with no data
        await kvSet(
          this.getUserDeviceDataKey(userId, deviceId),
          JSON.stringify({ ...deviceData, lastSync: Date.now() })
        );
      }
    } catch (error) {
      console.error('Error registering device:', error);
      throw new Error('Failed to register device');
    }
  }

  static async getRegisteredDeviceIds(userId: string): Promise<string[]> {
    try {
      return await kvGet<string[]>(this.getUserDeviceListKey(userId)) ?? [];
    } catch (error) {
      console.error('Error getting device IDs:', error);
      return [];
    }
  }

  static async getDeviceData(userId: string, deviceId: string): Promise<DeviceInfo | null> {
    try {
      const data = await kvGet<DeviceInfo>(this.getUserDeviceDataKey(userId, deviceId));
      return data;
    } catch (error) {
      console.error('Error getting device data:', error);
      return null;
    }
  }

  static async getRegisteredDevices(userId: string): Promise<DeviceInfo[]> {
    try {
      const deviceIds = await this.getRegisteredDeviceIds(userId);
      const devices = await Promise.all(
        deviceIds.map(deviceId => this.getDeviceData(userId, deviceId))
      );
      return devices.filter((device): device is DeviceInfo => device !== null);
    } catch (error) {
      console.error('Error getting registered devices:', error);
      return [];
    }
  }

  static async updateDeviceData(
    userId: string,
    deviceId: string,
    deviceData: DeviceInfo
  ): Promise<void> {
    try {
      await kvSet(
        this.getUserDeviceDataKey(userId, deviceId),
        JSON.stringify(deviceData)
      );
    } catch (error) {
      console.error('Error updating device data:', error);
      throw new Error('Failed to update device data');
    }
  }

  static async removeDevice(userId: string, deviceId: string): Promise<void> {
    try {
      // Remove from device list
      const deviceIds = await this.getRegisteredDeviceIds(userId);
      await kvSet(
        this.getUserDeviceListKey(userId),
        deviceIds.filter(id => id !== deviceId)
      );

      // Remove device data
      await kvDel(this.getUserDeviceDataKey(userId, deviceId));

      // Remove device sync data
      await kvDel(this.getDeviceSyncKey(userId, deviceId));
    } catch (error) {
      console.error('Error removing device:', error);
      throw new Error('Failed to remove device');
    }
  }


  // Check if device needs full sync
  static checkSyncStatus(lastSync?: number): {
    status: 'ok' | 'stale';
    requiresFullSync: boolean;
  } {
    try {
      if (!lastSync) {
        return { status: 'stale', requiresFullSync: true };
      }

      const sevenDaysAgo = Date.now() - (this.SYNC_WINDOW_DAYS * 24 * 60 * 60 * 1000);
      return {
        status: lastSync < sevenDaysAgo ? 'stale' : 'ok',
        requiresFullSync: lastSync < sevenDaysAgo
      };
    } catch (error) {
      console.error('Error checking sync status:', error);
      return { status: 'stale', requiresFullSync: true };
    }
  }

  // Record a change that needs syncing
  static async recordChange(
    metaInfo: SyncMetadata,
    dataKey: string,
    action: SyncAction,
    fetchOp: 'get' | 'zrange'
  ): Promise<void> {
    try {
      const event: SyncEvent = {
        type: metaInfo.type,
        entityId: metaInfo.entityId,
        userId: metaInfo.userId,
        createdBy: metaInfo.createdBy,
        action,
        dataKey,
        fetchOp,
        deviceId: getDeviceIdFromLocalStorage()
      };
      console.log('record change', this.getUserEventsKey(metaInfo.userId), event);
      await kvZadd<SyncEvent>(
        this.getUserEventsKey(metaInfo.userId),
        Date.now(),
        event
      );


    } catch (error) {
      console.error('Error recording change:', error);
      throw new Error('Failed to record change');
    }
  }

  // Get changes since last sync
  static async getChangesSinceLastSync(
    userId: string,
    lastSync: number | null
  ): Promise<SyncEvent[]> {
    try {
      if (!lastSync) return [];

      const events = await kvZrange<SyncEvent>(
        this.getUserEventsKey(userId),
        lastSync,
        Date.now(),
        { forceRefresh: true, method: 'score' }
      );

      // Cleanup old events
      const sevenDaysAgo = Date.now() - (this.SYNC_WINDOW_DAYS * 24 * 60 * 60 * 1000);
      await kvZremrangebyscore(this.getUserEventsKey(userId), 0, sevenDaysAgo);

      return events.map(x => x.member);
    } catch (error) {
      console.error('Error getting changes since last sync:', error);
      return [];
    }
  }

  static async syncKey(event: SyncEvent): Promise<boolean> {
    try {
      if (event.fetchOp === 'get') {
        return await kvGetSyncFromRemote(event.dataKey);
      } else if (event.fetchOp === 'zrange') {
        return await kvZoperationsSyncFromRemote(event.dataKey);
      }
      return false;
    } catch (error) {
      console.error(`Error syncing key ${event.dataKey}:`, error);
      return false;
    }
  }

  static async syncEvents(events: SyncEvent[]): Promise<boolean[]> {
    const results = await Promise.all(events.map(event => this.syncKey(event)));
    return results;
  }

}